// src/components/AdminDashboard.js
import React, { useState, useEffect, useCallback } from 'react';
import { API, Storage, Auth } from 'aws-amplify'; 
import { useNavigate } from 'react-router-dom';
import { FaTrashAlt, FaEdit, FaPlusCircle, FaCarSide, FaUsers } from 'react-icons/fa'; 

// Importaciones de mutaciones y queries
import { createVehicle, deleteVehicle } from '../graphql/mutations';
import { listVehicles } from '../graphql/queries'; 

// 🟢 Importamos awsExports para usar sus valores directamente en la URL si es necesario.
import awsExports from '../aws-exports'; 


// --- 🟢 FUNCIÓN AUXILIAR DE IMAGEN CORREGIDA PARA EL PANEL ADMIN ---
const getS3ImageUrl = (imageKey) => {
    if (!imageKey) return 'placeholder.jpg';
    
    // 🟢 USANDO EL BUCKET QUE YA SABEMOS QUE FUNCIONA
    const BUCKET_NAME = "amplify-vimcarbrokerwebnew-prodstable-cdd61-deployment"; 
    const BUCKET_REGION = "us-east-1"; 
    
    // Ruta pública corregida
    return `https://${BUCKET_NAME}.s3.${BUCKET_REGION}.amazonaws.com/public/${imageKey}`;
};
// -------------------------------------------------------------------------
// --- SUB-COMPONENTE: Publicar Vehículo (SIN CAMBIOS CRÍTICOS EN LÓGICA) ---
// -------------------------------------------------------------------------
const PublishVehicleForm = ({ currentUserId, onVehiclePublished, onCancel }) => {
    const [formData, setFormData] = useState({
        year: '', make: '', model: '', titleStatus: 'CLEAN',
        mileage: '', price: '', description: '', videoURL: '',
        isFeatured: false, isCashDeal: false, status: 'FOR_SALE'
    });
    const [imageFiles, setImageFiles] = useState([]);
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('info');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleImageChange = (e) => {
        setImageFiles(Array.from(e.target.files));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage('Subiendo archivos... por favor espere.');
        setMessageType('info');
        setIsSubmitting(true);

        try {
            // 1. Subir Múltiples Imágenes a Amazon S3
            const uploadedKeys = [];
            if (imageFiles.length === 0) {
                 throw new Error("Debe seleccionar al menos una imagen.");
            }
            
            // 🟢 La corrección del aws-exports.js debería hacer que esta llamada funcione.
            for (const file of imageFiles) {
                const imageKey = `vehicles/${currentUserId}/${Date.now()}_${file.name.replace(/ /g, "_")}`;
                await Storage.put(imageKey, file, {
                    level: 'public', 
                    contentType: file.type 
                });
                uploadedKeys.push(imageKey);
            }

            // 2. Crear el objeto de entrada para GraphQL
            const vehicleInput = {
                vehicleOwnerId: currentUserId, 
                
                year: formData.year ? parseInt(formData.year) : null,
                make: formData.make,
                model: formData.model,
                titleStatus: formData.titleStatus,
                mileage: formData.mileage ? parseInt(formData.mileage) : null,
                price: formData.price ? parseFloat(formData.price) : null,
                
                description: formData.description,
                videoURL: formData.videoURL || null, 
                
                isFeatured: formData.isFeatured,
                isCashDeal: formData.isCashDeal,
                status: formData.status,
                
                imageKey: uploadedKeys[0] || null, 
                allImageKeys: uploadedKeys.length > 0 ? uploadedKeys : null, 
            };

            // 3. Llamar a la mutación GraphQL (Usa AMAZON_COGNITO_USER_POOLS que YA CORREGISTE con tu rol)
            const result = await API.graphql({
                query: createVehicle,
                variables: { input: vehicleInput },
                authMode: 'AMAZON_COGNITO_USER_POOLS' 
            });

            const newVehicle = result.data.createVehicle;
            
            setMessage(`¡Vehículo (${newVehicle.make} ${newVehicle.model}) listado con éxito!`);
            setMessageType('success');
            onVehiclePublished(); 

        } catch (err) {
            console.error("Error al subir vehículo y/o guardar en DB:", err);
            
            let errorMessage = "Error al subir vehículo. Posiblemente un error de permisos (401/403) o campos no coincidentes en schema.";
            
            if (err.errors) {
                errorMessage = "Error de AppSync/GraphQL: " + err.errors.map(e => e.message).join(' | ');
            } else if (err.message) {
                 errorMessage = "Error de Subida (Storage/Red): " + err.message;
            }

            setMessage(errorMessage);
            setMessageType('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    // --- Estilos Omitidos por brevedad (sin cambios) ---
    const formStyles = {
        container: { padding: '20px', border: '1px solid #ccc', borderRadius: '8px', backgroundColor: '#fff', maxWidth: '800px', margin: '20px auto' },
        grid: { display: 'grid', gridTemplateColumns: window.innerWidth > 600 ? '1fr 1fr' : '1fr', gap: '20px' },
        inputGroup: { display: 'flex', flexDirection: 'column', marginBottom: '15px' },
        label: { marginBottom: '5px', fontWeight: 'bold' },
        input: { padding: '10px', border: '1px solid #ddd', borderRadius: '4px' },
        checkboxGroup: { display: 'flex', alignItems: 'center', marginBottom: '15px' },
        button: { padding: '10px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', marginTop: '20px' },
        message: { padding: '10px', margin: '15px 0', borderRadius: '4px', color: 'white', fontWeight: 'bold' },
        error: { backgroundColor: 'red' },
        success: { backgroundColor: 'green' },
        info: { backgroundColor: '#f1c40f', color: '#333' }
    };
    // --- Fin de Estilos Omitidos ---

    return (
        <div style={formStyles.container}>
            <h2>Publicar Nuevo Vehículo</h2>
            {message && (
                <div style={{ ...formStyles.message, ...(messageType === 'error' ? formStyles.error : (messageType === 'success' ? formStyles.success : formStyles.info)) }}>
                    {message}
                </div>
            )}
            <form onSubmit={handleSubmit}>
                <div style={formStyles.grid}>
                    {/* Campos Izquierda */}
                    <div>
                        <div style={formStyles.inputGroup}>
                            <label style={formStyles.label}>Marca (Make):</label>
                            <input type="text" name="make" value={formData.make} onChange={handleChange} style={formStyles.input} required />
                        </div>
                        <div style={formStyles.inputGroup}>
                            <label style={formStyles.label}>Modelo (Model):</label>
                            <input type="text" name="model" value={formData.model} onChange={handleChange} style={formStyles.input} required />
                        </div>
                        <div style={formStyles.inputGroup}>
                            <label style={formStyles.label}>Año (Year):</label>
                            <input type="number" name="year" value={formData.year} onChange={handleChange} style={formStyles.input} required />
                        </div>
                        <div style={formStyles.inputGroup}>
                            <label style={formStyles.label}>Millas (Mileage):</label>
                            <input type="number" name="mileage" value={formData.mileage} onChange={handleChange} style={formStyles.input} required />
                        </div>
                        <div style={formStyles.inputGroup}>
                            <label style={formStyles.label}>Precio ($):</label>
                            <input type="number" name="price" value={formData.price} onChange={handleChange} style={formStyles.input} required />
                        </div>
                        <div style={formStyles.inputGroup}>
                            <label style={formStyles.label}>Estado del Título:</label>
                            <select name="titleStatus" value={formData.titleStatus} onChange={handleChange} style={formStyles.input}>
                                <option value="CLEAN">CLEAN</option>
                                <option value="SALVAGE">SALVAGE</option>
                                <option value="REBUILT">REBUILT</option>
                            </select>
                        </div>
                    </div>
                    
                    {/* Campos Derecha */}
                    <div>
                        <div style={formStyles.inputGroup}>
                            <label style={formStyles.label}>Descripción:</label>
                            <textarea name="description" value={formData.description} onChange={handleChange} style={{ ...formStyles.input, height: '100px' }} required />
                        </div>
                        <div style={formStyles.inputGroup}>
                            <label style={formStyles.label}>Fotos del Vehículo (Múltiples):</label>
                            <input type="file" onChange={handleImageChange} multiple required />
                            <span>{imageFiles.length} archivo(s) seleccionado(s).</span>
                        </div>
                        <div style={formStyles.inputGroup}>
                            <label style={formStyles.label}>Video URL (opcional):</label>
                            <input type="text" name="videoURL" value={formData.videoURL} onChange={handleChange} style={formStyles.input} />
                        </div>

                        <div style={formStyles.checkboxGroup}>
                            <input type="checkbox" name="isFeatured" checked={formData.isFeatured} onChange={handleChange} id="isFeatured" />
                            <label htmlFor="isFeatured" style={{ marginLeft: '10px' }}>Destacado en Página Principal</label>
                        </div>
                        <div style={formStyles.checkboxGroup}>
                            <input type="checkbox" name="isCashDeal" checked={formData.isCashDeal} onChange={handleChange} id="isCashDeal" />
                            <label htmlFor="isCashDeal" style={{ marginLeft: '10px' }}>Sección Cash Deal</label>
                        </div>
                        <div style={formStyles.checkboxGroup}>
                            <input type="checkbox" 
                                name="status" 
                                checked={formData.status === 'SOLD'} 
                                onChange={(e) => setFormData(p => ({...p, status: e.target.checked ? 'SOLD' : 'FOR_SALE'}))} 
                                id="status" 
                            />
                            <label htmlFor="status" style={{ marginLeft: '10px' }}>Vendido (Status: SOLD)</label>
                        </div>
                    </div>
                </div>

                <button type="submit" disabled={isSubmitting} style={formStyles.button}>
                    {isSubmitting ? 'Subiendo y Publicando...' : 'Guardar y Publicar'}
                </button>
                <button type="button" onClick={onCancel} style={{...formStyles.button, backgroundColor: '#6c757d', marginLeft: '10px'}}>
                    Cancelar
                </button>
            </form>
        </div>
    );
};


// -------------------------------------------------------------------------
// --- SUB-COMPONENTE: Listado de Vehículos Admin (SIN CAMBIOS CRÍTICOS) ---
// -------------------------------------------------------------------------
const VehiclesListAdmin = ({ onEditVehicle, onNewVehicle }) => {
    const [vehicles, setVehicles] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    const handleEditVehicle = (vehicleId) => {
        navigate(`/admin/vehiculo/editar/${vehicleId}`); 
    };

    const fetchVehicles = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        
        let currentUserId = null;
        try {
            const user = await Auth.currentAuthenticatedUser();
            currentUserId = user.attributes.sub;
        } catch (authError) {
            setError("Error de autenticación. Por favor, cierra sesión y vuelve a iniciar.");
            setIsLoading(false);
            return;
        }

        try {
            const vehicleData = await API.graphql({
                query: listVehicles, 
                variables: { 
                    limit: 100, 
                },
                authMode: 'AMAZON_COGNITO_USER_POOLS' 
            });
            
            const fetchedVehicles = vehicleData.data.listVehicles.items || [];
            
            // Filtro local: Filtrar los vehículos del usuario actual
            const myVehicles = fetchedVehicles
                .filter(v => v.vehicleOwnerId === currentUserId) 
                .filter(v => v.status !== 'DELETED' && v.id)
                .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
            
            setVehicles(myVehicles); 
            setError(null); 
            
        } catch (err) {
            console.error("Error al cargar la lista de vehículos admin:", err);
            setError("Error en la consulta. Revise los permisos de AppSync/GraphQL. (401/403)");
        } finally {
            setIsLoading(false);
        }
    }, []); 

    useEffect(() => {
        fetchVehicles();
        const intervalId = setInterval(fetchVehicles, 30000); 
        return () => clearInterval(intervalId);
    }, [fetchVehicles]); 

    
    const handleDelete = async (vehicleId) => {
        if (!window.confirm("¿Estás seguro de que quieres eliminar este vehículo? Esta acción es irreversible.")) return;
        
        try {
            await API.graphql({
                query: deleteVehicle,
                variables: { input: { id: vehicleId } },
                authMode: 'AMAZON_COGNITO_USER_POOLS' 
            });
            fetchVehicles(); 
            alert("Vehículo eliminado con éxito.");
        } catch (err) {
            console.error("Error al eliminar vehículo:", err);
            alert("Error al eliminar el vehículo. Esto puede deberse a que el usuario autenticado no creó este registro.");
        }
    };

    const listStyles = {
        container: { padding: '20px', maxWidth: '1200px', margin: '20px auto' },
        table: { width: '100%', borderCollapse: 'collapse', marginTop: '20px' },
        th: { border: '1px solid #ddd', padding: '10px', textAlign: 'left', backgroundColor: '#f2f2f2' },
        td: { border: '1px solid #ddd', padding: '10px', verticalAlign: 'middle', fontSize: '14px' },
        button: { padding: '5px 10px', margin: '0 5px', cursor: 'pointer', borderRadius: '4px', fontSize: '12px' },
        editButton: { backgroundColor: '#ffc107', color: 'black' },
        deleteButton: { backgroundColor: '#dc3545', color: 'white' }
    };
    
    const getStatusColor = (status) => {
        switch (status) {
            case 'SOLD':
                return 'red'; 
            case 'FOR_SALE':
            default:
                return 'green'; 
        }
    };


    return (
        <div style={listStyles.container}>
            <h2>Inventario de Vehículos ({vehicles.length})</h2>
            {isLoading && <p>Cargando vehículos...</p>}
            {error && <p style={{color: 'red'}}>Error al cargar inventario: {error}</p>}
            <button onClick={onNewVehicle} style={{...listStyles.button, backgroundColor: '#28a745', color: 'white'}}>
                <FaPlusCircle style={{marginRight: '5px'}}/> Nuevo Vehículo
            </button>
            <table style={listStyles.table}>
                <thead>
                    <tr>
                        <th style={listStyles.th}>ID</th>
                        <th style={listStyles.th}>Foto</th>
                        <th style={listStyles.th}>Vehículo</th>
                        <th style={listStyles.th}>Precio</th>
                        <th style={listStyles.th}>Destacado</th>
                        <th style={listStyles.th}>Estado</th>
                        <th style={listStyles.th}>Acciones</th>
                    </tr>
                </thead>
                <tbody>
                    {vehicles.map((v) => (
                        <tr key={v.id}>
                            <td style={listStyles.td}>{v.id.substring(0, 8)}...</td>
                            <td style={listStyles.td}>
                                {/* Usamos la función corregida que ahora usa aws-exports */}
                                <img src={getS3ImageUrl(v.imageKey)} alt="Foto" style={{ width: '50px', height: '50px', objectFit: 'cover' }} />
                            </td>
                            <td style={listStyles.td}>{v.year} {v.make} {v.model}</td>
                            <td style={listStyles.td}>${v.price?.toLocaleString()}</td>
                            <td style={listStyles.td}>{v.isFeatured ? 'Sí' : 'No'}</td>
                            <td style={listStyles.td}>
                                <span style={{ color: getStatusColor(v.status), fontWeight: 'bold' }}>
                                    {v.status}
                                </span>
                            </td>
                            <td style={listStyles.td}>
                                <button 
                                    onClick={() => handleEditVehicle(v.id)} 
                                    style={{...listStyles.button, ...listStyles.editButton}}
                                >
                                    <FaEdit /> Editar
                                </button>
                                <button 
                                    onClick={() => handleDelete(v.id)} 
                                    style={{...listStyles.button, ...listStyles.deleteButton}}
                                >
                                    <FaTrashAlt /> Eliminar
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};


// -------------------------------------------------------------------------
// --- COMPONENTE PRINCIPAL AdminDashboard (SIN CAMBIOS CRÍTICOS) ---
// -------------------------------------------------------------------------
export default function AdminDashboard({ user, isAdmin, onSignOut }) {
    const [view, setView] = useState('list'); // 'list' o 'publish'
    const [currentUserId, setCurrentUserId] = useState(null);

    // Obtener el ID del usuario actual al cargar
    useEffect(() => {
        const getUserId = async () => {
            try {
                const authUser = await Auth.currentAuthenticatedUser();
                // El sub es el ID único del usuario en Cognito
                setCurrentUserId(authUser.attributes.sub); 
            } catch (e) {
                console.error("No se pudo obtener el ID de usuario:", e);
                // Si falla la autenticación, redirige al login, aunque RequireAuth ya debería haberlo manejado
            }
        };
        getUserId();
    }, []);

    // 🛑 Este es el chequeo de acceso que debe pasar.
    if (!isAdmin) {
        return (
            <div style={{ textAlign: 'center', padding: '50px', color: 'red', fontWeight: 'bold' }}>
                <p>⚠️ ACCESO DENEGADO ⚠️</p>
                <p>Tu usuario no tiene los permisos de Administrador (Cognito Group: ADMIN) para acceder a esta página.</p>
                <button onClick={onSignOut} style={{ padding: '10px 20px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', marginTop: '20px' }}>
                    Cerrar Sesión e Intentar con otra cuenta
                </button>
            </div>
        );
    }

    const renderContent = () => {
        switch (view) {
            case 'publish':
                return (
                    <PublishVehicleForm 
                        currentUserId={currentUserId}
                        onVehiclePublished={() => setView('list')}
                        onCancel={() => setView('list')}
                    />
                );
            case 'leads':
                return <div style={{textAlign: 'center', padding: '50px'}}>Módulo de Leads - Próximamente</div>;
            case 'list':
            default:
                return (
                    <VehiclesListAdmin 
                        onEditVehicle={() => setView('list')} 
                        onNewVehicle={() => setView('publish')}
                    />
                );
        }
    };
    
    // Si el ID del usuario no está cargado, no mostramos nada aún
    if (!currentUserId) {
        return <div style={{ textAlign: 'center', padding: '50px' }}>Cargando datos del usuario...</div>;
    }


    return (
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '20px' }}>
            <h1 style={{ color: '#007bff', borderBottom: '2px solid #eee', paddingBottom: '10px' }}>Panel de Administración</h1>

            <div style={{ display: 'flex', gap: '10px', marginBottom: '30px' }}>
                <button onClick={() => setView('list')} style={{ padding: '10px 20px', backgroundColor: view === 'list' ? '#007bff' : '#ccc', color: view === 'list' ? 'white' : 'black', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                    <FaCarSide style={{marginRight: '5px'}}/> Inventario
                </button>
                <button onClick={() => setView('leads')} style={{ padding: '10px 20px', backgroundColor: view === 'leads' ? '#007bff' : '#ccc', color: view === 'leads' ? 'white' : 'black', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                    <FaUsers style={{marginRight: '5px'}}/> Leads
                </button>
                <button onClick={onSignOut} style={{ padding: '10px 20px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', marginLeft: 'auto' }}>
                    Cerrar Sesión
                </button>
            </div>

            {renderContent()}

        </div>
    );
}